import React, { useMemo, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, Platform, type TextStyle } from 'react-native';
import Animated, { useSharedValue, useAnimatedStyle, withTiming, interpolate, type SharedValue } from 'react-native-reanimated';
import moment from 'moment';

import { useAuthNavigation } from '../../../../shared/navigation/useAuthNavigation';
import { Clickable2 } from '../../../../components/common/Clickable2';
import { SvgIcon } from '../../../../components/common/SvgIcon';
import { useTranslation } from '../../../../shared/i18n';
import { useRtl } from '../../../../shared/i18n/useRtl';
import { appTheme, useThemeColors } from '../../../../shared/theme';
import { DASH_STUDIO_INNER } from '../../../../shared/theme/studio/dashboardConstants';
import type { FlatItem } from '../../DashboardMenuImagesStrip';

const TODAY_IMAGE_MAX = 3;
const FALLBACK_IMAGE_MAX = 6;
const FADE_INTERVAL_MS = 4000;
const SLIDE_DURATION_MS = 400;
const SLIDE_OFFSET_PX = 60;
const INNER_RADIUS = 14;
const IMAGE_SIZE = 52;
const ANDROID_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

export interface DashboardClickBannerSet2Props {
  today_menu_strip_items?: FlatItem[];
  fallback_menu_strip_items?: FlatItem[];
}

export function DashboardClickBannerSet2({
  today_menu_strip_items = [],
  fallback_menu_strip_items = [],
}: DashboardClickBannerSet2Props) {
  const { t } = useTranslation();
  const { isRTL } = useRtl();
  const navigation = useAuthNavigation();
  const c = useThemeColors() ?? appTheme.colors;

  const display_uris = useMemo(() => {
    const today_uris = (today_menu_strip_items ?? [])
      .filter((i): i is FlatItem & { uri: string } => Boolean(i.uri))
      .slice(0, TODAY_IMAGE_MAX)
      .map(i => i.uri);
    if (today_uris.length > 0) {
      return today_uris;
    }
    return (fallback_menu_strip_items ?? [])
      .filter((i): i is FlatItem & { uri: string } => Boolean(i.uri))
      .slice(0, FALLBACK_IMAGE_MAX)
      .map(i => i.uri);
  }, [today_menu_strip_items, fallback_menu_strip_items]);

  const active_index = useSharedValue(0);
  const should_loop = display_uris.length > 1;

  useEffect(() => {
    if (!should_loop || display_uris.length <= 1) {
      return;
    }
    const id = setInterval(() => {
      const next = (Math.round(active_index.value) + 1) % display_uris.length;
      active_index.value = withTiming(next, { duration: SLIDE_DURATION_MS });
    }, FADE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [should_loop, display_uris.length, active_index]);

  const on_explore = useCallback(() => {
    navigation.navigate('DailyMenuFlow', {
      date: moment().format('YYYY-MM-DD'),
      fromCalendar: true,
    });
  }, [navigation]);

  return (
    <View style={styles.banner}>
      {display_uris.length > 0 ? (
        <View style={styles.thumb} pointerEvents="none">
          {should_loop ? (
            display_uris.map((uri, i) => (
              <MenuImageLayer key={`${uri}-${i}`} uri={uri} index={i} active_index={active_index} slide_rtl={isRTL} />
            ))
          ) : (
            <Animated.Image source={{ uri: display_uris[0] }} style={styles.thumbImage} resizeMode="cover" />
          )}
        </View>
      ) : null}

      <Clickable2
        onPress={on_explore}
        accessibilityLabel={t('dashboard.exploreDishes')}
        accessibilityRole="button"
        haptics
        style={[styles.cta, { backgroundColor: c.primary }]}>
        <SvgIcon name="calendar" size={appTheme.sizes.smallIcon} color={c.onPrimary} />
        <Text style={[styles.ctaLabel, { color: c.onPrimary }, ANDROID_TEXT]} numberOfLines={1}>
          {t('dashboard.exploreDishes')}
        </Text>
      </Clickable2>
    </View>
  );
}

function MenuImageLayer({
  uri,
  index,
  active_index,
  slide_rtl,
}: {
  uri: string;
  index: number;
  active_index: SharedValue<number>;
  slide_rtl: boolean;
}) {
  const slide_sign = slide_rtl ? -1 : 1;
  const animated_style = useAnimatedStyle(() => {
    'worklet';
    const a = active_index.value;
    const opacity = interpolate(a, [index - 0.5, index, index + 0.5], [0, 1, 0]);
    const off = SLIDE_OFFSET_PX * slide_sign;
    const translate_x = interpolate(a, [index - 1, index, index + 1], [off, 0, -off]);
    return {
      opacity,
      transform: [{ translateX: translate_x }],
    };
  }, [slide_sign]);
  return <Animated.Image source={{ uri }} style={[styles.thumbImage, styles.thumbImageAbs, animated_style]} resizeMode="cover" />;
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    padding: DASH_STUDIO_INNER,
    gap: appTheme.spacing.md,
  },
  thumb: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: INNER_RADIUS,
    overflow: 'hidden',
    flexGrow: 0,
    flexShrink: 0,
  },
  thumbImage: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: INNER_RADIUS,
  },
  thumbImageAbs: {
    position: 'absolute',
    top: 0,
    start: 0,
  },
  /** Clickable2 host fills leftover width (flex, not %). */
  cta: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    height: IMAGE_SIZE,
    borderRadius: INNER_RADIUS,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.sm,
    paddingHorizontal: DASH_STUDIO_INNER,
  },
  ctaLabel: {
    ...appTheme.typography.bodyMedium,
    fontWeight: '600',
    flexShrink: 1,
    textAlign: 'center',
  },
});
